import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { taskService } from '../../services/taskService';
import categoryService, { Category } from '../../services/categoryService';
import tagService, { Tag } from '../../services/tagService';
import { CreateTaskDTO } from '../../types/task';
import { translateCategory } from '../../utils/categoryTranslations';
import { translateTag } from '../../utils/tagTranslations';
import FileUpload, { UploadedFile } from '../../components/FileUpload';
import PageBackButton from '../../components/PageBackButton';

const CreateTask: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [createdTaskId, setCreatedTaskId] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [categoriesData, tagsData] = await Promise.all([
          categoryService.getCategories(),
          tagService.getTags()
        ]);
        setCategories(categoriesData.items);
        setTags(tagsData.items);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(t('common.error_loading'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const getLocale = () => getDateFnsLocale(i18n.language);

  const { register, handleSubmit, formState: { errors }, watch, setValue, control } = useForm<CreateTaskDTO>();
  const localizedCategories = useMemo(() => {
    const dedupedByLabel = new Map<string, Category>();

    categories.forEach((category) => {
      const localizedName = translateCategory(category.name, i18n.language);
      const dedupeKey = localizedName.trim().toLowerCase();
      if (!dedupedByLabel.has(dedupeKey)) {
        dedupedByLabel.set(dedupeKey, category);
      }
    });

    return Array.from(dedupedByLabel.values()).sort((left, right) =>
      translateCategory(left.name, i18n.language).localeCompare(
        translateCategory(right.name, i18n.language),
        i18n.language
      )
    );
  }, [categories, i18n.language]);

  const localizedTags = useMemo(() => {
    const dedupedByLabel = new Map<string, Tag>();

    tags.forEach((tag) => {
      const localizedName = translateTag(tag.name, i18n.language);
      const dedupeKey = localizedName.trim().toLowerCase();
      if (!dedupedByLabel.has(dedupeKey)) {
        dedupedByLabel.set(dedupeKey, tag);
      }
    });

    return Array.from(dedupedByLabel.values()).sort((left, right) =>
      translateTag(left.name, i18n.language).localeCompare(
        translateTag(right.name, i18n.language),
        i18n.language
      )
    );
  }, [tags, i18n.language]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryIdParam = params.get('category_id');
    if (!categoryIdParam) {
      return;
    }

    const parsed = Number(categoryIdParam);
    if (!Number.isNaN(parsed) && parsed > 0) {
      setValue('category_id', parsed);
    }
  }, [location.search, setValue]);

  const onSubmit = async (data: CreateTaskDTO) => {
    try {
      setIsSubmitting(true);
      setError(null);
      const newTask = await taskService.createTask(data);
      setCreatedTaskId(newTask.id);
      setSuccess(true);
      setTimeout(() => {
        navigate('/tasks/my');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('tasks.create.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = (files: UploadedFile[]) => {
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  return (
    <Container maxWidth="md" className="py-8">
      <PageBackButton />

      <Paper className="p-6">
        <Typography variant="h5" component="h1" className="mb-6">
          {t('tasks.create.title')}
        </Typography>

        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" className="mb-4">
            {t('tasks.create.success')}
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <TextField
              fullWidth
              label={t('tasks.create.form.title')}
              error={!!errors.title}
              helperText={errors.title?.message}
              disabled={isSubmitting}
              {...register('title', {
                required: t('tasks.create.form.title_required'),
                minLength: {
                  value: 5,
                  message: t('tasks.create.form.title_min_length')
                }
              })}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('tasks.create.form.description')}
              error={!!errors.description}
              helperText={errors.description?.message}
              disabled={isSubmitting}
              {...register('description', {
                required: t('tasks.create.form.description_required'),
                minLength: {
                  value: 20,
                  message: t('tasks.create.form.description_min_length')
                }
              })}
            />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('tasks.create.form.budget')}
                  error={!!errors.budget}
                  helperText={errors.budget?.message}
                  disabled={isSubmitting}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">EUR</InputAdornment>
                  }}
                  {...register('budget', {
                    required: t('tasks.create.form.budget_required'),
                    min: {
                      value: 1,
                      message: t('tasks.create.form.budget_min')
                    }
                  })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getLocale()}>
                  <DateTimePicker
                    label={t('tasks.create.form.deadline')}
                    value={new Date(watch('deadline'))}
                    onChange={(date) => setValue('deadline', date?.toISOString() || new Date().toISOString())}
                    disabled={isSubmitting}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.deadline,
                        helperText: errors.deadline?.message
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label={t('tasks.create.form.address')}
              error={!!errors.address}
              helperText={errors.address?.message}
              disabled={isSubmitting}
              {...register('address', {
                required: t('tasks.create.form.address_required')
              })}
            />

            <FormControl fullWidth>
              <InputLabel id="category-label">{t('tasks.create.form.category')}</InputLabel>
              <Controller
                name="category_id"
                control={control}
                render={({ field }) => (
                  <Select
                    labelId="category-label"
                    label={t('tasks.create.form.category')}
                    disabled={isSubmitting}
                    {...field}
                    value={field.value || ''}
                  >
                    <MenuItem value="">{t('tasks.create.form.no_category')}</MenuItem>
                    {localizedCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {translateCategory(category.name, i18n.language)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>

            <Controller
              name="tag_ids"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.tag_ids}>
                  <InputLabel id="tags-label">{t('tasks.create.form.tags')}</InputLabel>
                  <Select
                    labelId="tags-label"
                    multiple
                    {...field}
                    renderValue={(selected: number[]) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value: number) => {
                          const tag = localizedTags.find(t => t.id === value) || tags.find(t => t.id === value);
                          return (
                            <Chip key={value} label={tag ? translateTag(tag.name, i18n.language) : value} />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {localizedTags.map((tag) => (
                      <MenuItem key={tag.id} value={tag.id}>
                        {translateTag(tag.name, i18n.language)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.tag_ids && (
                    <FormHelperText>{errors.tag_ids.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="show_metrics_to_professionals"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(field.value)}
                      onChange={(event) => field.onChange(event.target.checked)}
                      disabled={isSubmitting}
                    />
                  }
                  label={t('tasks.create.form.show_metrics_to_professionals')}
                />
              )}
            />
            <Typography variant="body2" color="text.secondary">
              {t('tasks.create.form.show_metrics_to_professionals_hint')}
            </Typography>

            {/* Privacy: клиентът сам избира какви лични данни да са видими */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('tasks.create.form.privacy_section')}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('tasks.create.form.privacy_section_hint')}
              </Typography>

              <Controller
                name="show_client_phone_to_professionals"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(field.value)}
                        onChange={(event) => field.onChange(event.target.checked)}
                        disabled={isSubmitting}
                      />
                    }
                    label={t('tasks.create.form.show_client_phone')}
                  />
                )}
              />

              <Controller
                name="show_client_email_to_professionals"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(field.value)}
                        onChange={(event) => field.onChange(event.target.checked)}
                        disabled={isSubmitting}
                      />
                    }
                    label={t('tasks.create.form.show_client_email')}
                  />
                )}
              />

              <Controller
                name="show_client_location_to_professionals"
                control={control}
                defaultValue={true}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(field.value)}
                        onChange={(event) => field.onChange(event.target.checked)}
                        disabled={isSubmitting}
                      />
                    }
                    label={t('tasks.create.form.show_client_location')}
                  />
                )}
              />
            </Box>

            {/* File Upload Section */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('tasks.create.form.attachments', 'Прикачени файлове')}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('tasks.create.form.attachments_hint', 'Качете снимки или документи, които помагат да се разбере задачата')}
              </Typography>
              <FileUpload
                taskId={createdTaskId || undefined}
                multiple={true}
                onUploadComplete={handleFileUpload}
              />
            </Box>

            <Box className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting || success}
                startIcon={isSubmitting && <CircularProgress size={20} />}
              >
                {t('common.submit')}
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </Container>
  );
};

export default CreateTask; 